import type { AuthenticationModesResolutionFailedAction } from '@Application/Security/Sso/Actions/authenticationModesResolutionFailed';
import type { AuthenticationModesResolvedAction } from '@Application/Security/Sso/Actions/authenticationModesResolved';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { ConnectionTestedAction } from '@Application/Security/Sso/Actions/connectionTested';
import type { IdentityProviderDeletedAction } from '@Application/Security/Sso/Actions/identityProviderDeleted';
import type { IdentityProviderFetchedAction } from '@Application/Security/Sso/Actions/identityProviderFetched';
import type { IdentityProviderSavedAction } from '@Application/Security/Sso/Actions/identityProviderSaved';
import type { PasswordSignInUserCountFetchedAction } from '@Application/Security/Sso/Actions/passwordSignInUserCountFetched';
import AuthenticationModesView from '@UI/Store/Sso/Views/AuthenticationModesView';
import ConnectionTestView from '@UI/Store/Sso/Views/ConnectionTestView';
import IdentityProviderView from '@UI/Store/Sso/Views/IdentityProviderView';
import PasswordSignInUserCountView from '@UI/Store/Sso/Views/PasswordSignInUserCountView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

// Replaces a nullable `identityProvider` field: `null` used to mean both "never fetched" and "fetched,
// this account has none", which are two very different states for a screen deciding between a skeleton
// and an empty state. The discriminant carries the resolved account on every branch but `unresolved`, so
// a mutation handler can compare it against an action's `accountId` without a second field.
export type IdentityProviderResolution =
	| { type: 'unresolved' }
	| { type: 'absent'; accountId: number }
	| { type: 'configured'; accountId: number; identityProvider: IdentityProviderView };

export type SsoStore = {
	authenticationModes: AuthenticationModesView | null;
	identityProviderResolution: IdentityProviderResolution;
	connectionTest: ConnectionTestView | null;
	passwordSignInUserCount: PasswordSignInUserCountView | null;
};

const initialState: SsoStore = {
	authenticationModes: null,
	identityProviderResolution: { type: 'unresolved' },
	connectionTest: null,
	passwordSignInUserCount: null,
};

type Actions =
	| AuthenticationModesResolvedAction
	| AuthenticationModesResolutionFailedAction
	| IdentityProviderFetchedAction
	| IdentityProviderSavedAction
	| IdentityProviderDeletedAction
	| ConnectionTestedAction
	| PasswordSignInUserCountFetchedAction
	| UserRevokedAction;

const whenAuthenticationModesResolved = (
	store: SsoStore,
	action: AuthenticationModesResolvedAction,
): SsoStore => ({
	...store,
	authenticationModes: new AuthenticationModesView(action.payload, action.email),
});

// A failed resolution must not leave a previous address's modes in place: the screen reads this `null`
// as its cue to fall back.
const whenAuthenticationModesResolutionFailed = (store: SsoStore): SsoStore => ({
	...store,
	authenticationModes: null,
});

const whenIdentityProviderFetched = (store: SsoStore, action: IdentityProviderFetchedAction): SsoStore => ({
	...store,
	identityProviderResolution:
		action.payload.identityProvider === null
			? { type: 'absent', accountId: action.accountId }
			: {
					type: 'configured',
					accountId: action.accountId,
					identityProvider: new IdentityProviderView(action.payload.identityProvider),
				},
});

const whenIdentityProviderSaved = (store: SsoStore, action: IdentityProviderSavedAction): SsoStore => {
	if (
		store.identityProviderResolution.type === 'unresolved' ||
		store.identityProviderResolution.accountId !== action.accountId
	) {
		return store;
	}

	return {
		...store,
		identityProviderResolution: {
			type: 'configured',
			accountId: action.accountId,
			identityProvider: new IdentityProviderView(action.payload),
		},
	};
};

const whenIdentityProviderDeleted = (store: SsoStore, action: IdentityProviderDeletedAction): SsoStore => {
	if (
		store.identityProviderResolution.type === 'unresolved' ||
		store.identityProviderResolution.accountId !== action.accountId
	) {
		return store;
	}

	// The screen must fall back to its empty state, never restart an infinite loading loop: the
	// resolution becomes `absent` for this account, not `unresolved` — a further fetch would only
	// relearn what is already known.
	return {
		...store,
		identityProviderResolution: { type: 'absent', accountId: action.accountId },
		connectionTest: null,
	};
};

const whenConnectionTested = (store: SsoStore, action: ConnectionTestedAction): SsoStore => {
	if (
		store.identityProviderResolution.type === 'unresolved' ||
		store.identityProviderResolution.accountId !== action.accountId
	) {
		return store;
	}

	return {
		...store,
		// Updates `lastTestedAt` / `lastTestSucceeded` on the existing configuration without discarding
		// its other fields: otherwise the screen would keep showing the previous test's result. An
		// `absent` resolution has no configuration to update, so it passes through unchanged.
		identityProviderResolution:
			store.identityProviderResolution.type === 'configured'
				? {
						...store.identityProviderResolution,
						identityProvider:
							store.identityProviderResolution.identityProvider.withConnectionTestResult(
								action.payload.succeeded,
								action.payload.testedAt,
							),
					}
				: store.identityProviderResolution,
		connectionTest: new ConnectionTestView(action.payload),
	};
};

const whenPasswordSignInUserCountFetched = (
	store: SsoStore,
	action: PasswordSignInUserCountFetchedAction,
): SsoStore => ({
	...store,
	passwordSignInUserCount: new PasswordSignInUserCountView(action.payload, action.accountId),
});

// A configuration's `issuerUrl`, `clientId` and email domains must not survive a logout and be served
// to the next person on the same browser — the same reasoning as every other store in the repository.
const whenUserRevoked = (): SsoStore => initialState;

export default registerStore<'ssoStore', Actions>(
	'ssoStore',
	createSliceReducer({
		AUTHENTICATION_MODES_RESOLVED: whenAuthenticationModesResolved,
		AUTHENTICATION_MODES_RESOLUTION_FAILED: whenAuthenticationModesResolutionFailed,
		IDENTITY_PROVIDER_FETCHED: whenIdentityProviderFetched,
		IDENTITY_PROVIDER_SAVED: whenIdentityProviderSaved,
		IDENTITY_PROVIDER_DELETED: whenIdentityProviderDeleted,
		CONNECTION_TESTED: whenConnectionTested,
		PASSWORD_SIGN_IN_USER_COUNT_FETCHED: whenPasswordSignInUserCountFetched,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
